import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import DivisionSelector from '@/components/DivisionSelector';
import { supabase } from '@/lib/supabaseClient';
import { Fixture, FixtureStatus } from '@/types/fixtures';

// Extend Fixture to allow allowEdit property
type FixtureWithEdit = Fixture & { allowEdit?: boolean };
import FixtureResults from '@/components/FixtureResults';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import FixturesList from '@/components/FixturesList';
import AddHomeFixtureModal from '@/components/AddHomeFixtureModal';
import { Button } from '@/components/ui/button';

// Corrected imports for types
import { Division } from '@/types/divisions';
import { Team } from '@/types/teams'; // Ensure club_id exists in the Team type definition

const slugify = (text: string) =>
  text
    .toLowerCase()
    .replace(/&/g, 'and') // Replace ampersands
    .replace(/\./g, '')   // Remove periods like in "St."
    .replace(/'/g, '')    // Remove apostrophes
    .replace(/\s+/g, '-') // Replace spaces with dashes
    .replace(/[^\w\-]+/g, '') // Remove other non-word characters
    .replace(/\-\-+/g, '-')   // Replace multiple dashes with a single dash
    .replace(/^-+|-+$/g, ''); // Trim dashes from start/end

const FixturesPage: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const fixturesRef = useRef<HTMLDivElement | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const queryDivisionSlug = searchParams.get('division');
  const queryTeamSlug = searchParams.get('team');

  const navigate = useNavigate();
  const location = useLocation();

  // Type the states as Division object for selectedDivision and string for selectedTeam
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [teams, setTeams] = useState<(Team & { is_lowest_team: boolean; players?: { id: string; name: string }[] })[]>([]);
  const [selectedDivision, setSelectedDivision] = useState<Division | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null); // Ensure this is a string or null
  const [resultFixture, setResultFixture] = useState<FixtureWithEdit | null>(null);
  const [isAddFixtureOpen, setIsAddFixtureOpen] = useState(false);
  const [seasonId, setSeasonId] = useState<string>('');
  const [refreshFixtures, setRefreshFixtures] = useState(false);
  const [fixtureToEdit, setFixtureToEdit] = useState<Fixture | null>(null);
  const [fixtures, setFixtures] = useState<Fixture[]>([]);

  useEffect(() => {
    const fetchSeasons = async () => {
      const { data, error } = await supabase.from('seasons').select('id').order('created_at', { ascending: false }).limit(1);
      if (error) {
        console.error('❌ Error fetching season:', error.message);
      } else if (data && data.length > 0) {
        setSeasonId(data[0].id);
      }
    };

    fetchSeasons();
  }, []);

  useEffect(() => {
    const fetchDivisions = async () => {
      const { data, error } = await supabase.from('divisions').select('id, name');
      if (error) {
        console.error('❌ Error fetching divisions:', error.message);
      } else {
        const sorted = (data ?? []).sort((a, b) => a.name.localeCompare(b.name));
        setDivisions(sorted);
        if (sorted.length > 0) {
          setSelectedDivision(sorted[0]);
        }
        if (sorted.length > 0 && queryDivisionSlug) {
          const matchingDivision = sorted.find(div => slugify(div.name) === queryDivisionSlug);
          if (matchingDivision) {
            setSelectedDivision(matchingDivision);
          }
        }
      }
    };

    fetchDivisions();
  }, []);

  useEffect(() => {
    const fetchTeams = async () => {
      if (!selectedDivision) return;

      const { data, error } = await supabase
        .from('teams')
        .select('id, name, division_id, project_id, club_id, is_lowest_team, players(id, name)')
        .eq('division_id', selectedDivision.id);

      if (error) {
        console.error('❌ Error fetching teams:', error.message);
      } else {
        const sortedTeams = (data ?? []).sort((a, b) => a.name.localeCompare(b.name));
        setTeams(sortedTeams);
        if (sortedTeams.length > 0) {
          setSelectedTeam(sortedTeams[0].id); // Select the first team
        }
        if (sortedTeams.length > 0 && queryTeamSlug) {
          const matchingTeam = sortedTeams.find(team => slugify(team.name) === queryTeamSlug);
          if (matchingTeam) {
            setSelectedTeam(matchingTeam.id);
          }
        }
      }
    };

    fetchTeams();
  }, [selectedDivision]);

  const filteredFixtures = fixtures.filter((fixture) =>
    (fixture.home_team.division_id === selectedDivision?.id || fixture.away_team.division_id === selectedDivision?.id) &&
    (fixture.home_team.id === selectedTeam || fixture.away_team.id === selectedTeam)
  );

  // Fetch fixtures with results and matchups when division, season, or refresh changes
  useEffect(() => {
    const fetchFixtures = async () => {
      if (!selectedDivision || !seasonId) return;

      const { data, error } = await supabase
        .from('fixtures')
        .select(`
          *,
          home_team:teams!fixtures_home_team_id_fkey (
            id, name, division_id, club_id
          ),
          away_team:teams!fixtures_away_team_id_fkey (
            id, name, division_id, club_id
          ),
          results (
            id,
            home_score,
            away_score,
            submitted_by,
            submitted_at,
            matchups (
              id,
              result,
              home_player:home_player_id (
                id,
                name
              ),
              away_player:away_player_id (
                id,
                name
              )
            )
          )
        `)
        .eq('division_id', selectedDivision.id)
        .eq('season_id', seasonId);

      if (error) {
        console.error('❌ Error fetching fixtures:', error.message);
      } else {
        setFixtures(data ?? []);
      }
    };

    fetchFixtures();
  }, [selectedDivision, seasonId, refreshFixtures]);

  // Effect to check for ?fixture= param and open result dialog if present
  useEffect(() => {
    const fixtureId = searchParams.get('fixture');

    // Clear lingering fixture param if no match found OR selected team doesn't match
    const matched = fixtures.find(f => f.id === fixtureId);
    const teamInFixture = matched && (
      matched.home_team.id === selectedTeam ||
      matched.away_team.id === selectedTeam
    );

    if (!fixtureId || !matched || !teamInFixture) {
      setResultFixture(null);
      const params = new URLSearchParams(searchParams);
      params.delete('fixture');
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
      return;
    }

    setResultFixture({
      ...matched,
      allowEdit: matched.status === 'scheduled',
    });
  }, [searchParams, fixtures, teams, selectedTeam]);

  const handleDivisionChange = (division: Division) => {
    setSelectedDivision(division);
    setSelectedTeam(null); // Reset selected team when division changes
    const params = new URLSearchParams(searchParams);
    params.set('division', slugify(division.name));
    params.delete('team'); // Reset team when division changes
    setSearchParams(params);
  };

  const handleEnterResult = (fixture: Fixture) => {
    const isEditable = fixture.status === 'scheduled';
    setResultFixture({ ...fixture, allowEdit: isEditable });

    const params = new URLSearchParams(searchParams);
    params.set('fixture', fixture.id);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const handleFixtureAdded = (keepModalOpen: boolean = false) => {
    setRefreshFixtures((prev) => !prev);

    if (!keepModalOpen) {
      setIsAddFixtureOpen(false);
      setFixtureToEdit(null);
    }
  };

  const handleEditFixture = (fixture: Fixture) => {
    setFixtureToEdit(fixture);
    setIsAddFixtureOpen(true);
  };

  const handleDeleteFixture = () => {
    setRefreshFixtures((prev) => !prev);
  };

const selectedTeamObject = teams.find((team) => team.id === selectedTeam);
// Only compute canAddFixture after both user and selectedTeamObject are defined
const canAddFixture =
  user &&
  selectedTeamObject &&
  (
    user?.role === 'admin' ||
    (user?.role === 'captain' &&
      selectedTeamObject?.club_id &&
      user?.club_id &&
      selectedTeamObject.club_id.toString() === user.club_id.toString())
  );

  const extractTeamNumber = (name: string): number | null => {
    const romanToInt: Record<string, number> = {
      I: 1, II: 2, III: 3, IV: 4, V: 5, VI: 6, VII: 7, VIII: 8, IX: 9, X: 10,
    };

    const match = name.trim().match(/\b(I|II|III|IV|V|VI|VII|VIII|IX|X)\b/);
    if (match && match[1]) {
      return romanToInt[match[1]] || null;
    }

    // Fallback to numeric extraction
    const numMatch = name.match(/\d+/);
    return numMatch ? parseInt(numMatch[0], 10) : null;
  };

  return (
    <>
      <div className="space-y-4">
      <DivisionSelector
        divisions={divisions}
        selectedDivision={selectedDivision}
        selectedTeam={selectedTeam}
        onSelectDivision={handleDivisionChange}
        onSelectTeam={(teamId) => {
          setSelectedTeam(teamId);
          setResultFixture(null); // Clear any open result fixture
          const params = new URLSearchParams(searchParams);
          if (teamId && selectedDivision && projectSlug) {
            const teamObj = teams.find(t => t.id === teamId);
            if (teamObj) {
              params.set('team', slugify(teamObj.name));
              params.set('division', slugify(selectedDivision.name));
            }
          }
          setSearchParams(params);
          setTimeout(() => {
            fixturesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);
        }}
        teamOptions={teams}
      />

      {/* Debugging output removed */}
      {user && selectedTeamObject && canAddFixture && (
        <div className="text-right">
          <Button size="sm" onClick={() => setIsAddFixtureOpen(true)} className="bg-[#30702F] hover:bg-[#255123] text-white">
            Add Home Fixture
          </Button>
        </div>
      )}

      <div ref={fixturesRef}>
        {selectedTeam && selectedDivision && seasonId && (
          <FixturesList
            selectedDivision={selectedDivision?.id || ''}
            selectedTeam={selectedTeamObject?.id || ''}
            seasonId={seasonId}
            teams={teams}
            projectSlug={projectSlug}
            onEnterResult={handleEnterResult}
            refreshKey={refreshFixtures}
            onEditFixture={handleEditFixture}
            onDeleteFixture={handleDeleteFixture}
            fixtures={filteredFixtures}
            clubs={[]}
          />
        )}
      </div>

      {resultFixture && (
        <Dialog open={!!resultFixture} onOpenChange={(open) => {
          if (!open) {
            setResultFixture(null);
            const params = new URLSearchParams(searchParams);
            params.delete('fixture');
            navigate(`${location.pathname}?${params.toString()}`, { replace: true });
          }
        }}>
          <DialogContent
            aria-describedby="fixture-results-description"
            className="max-w-3xl"
          >
            <DialogTitle>Enter Fixture Result</DialogTitle>
            <div id="fixture-results-description" className="sr-only">
              Enter result details for the selected fixture.
            </div>
            <FixtureResults
              fixture={{
                ...resultFixture,
                home_team: {
                  ...resultFixture.home_team,
                  club_id: resultFixture.home_team.club_id,
                  players: teams
                    .filter((t) => t.club_id === resultFixture.home_team.club_id)
                    .flatMap((t) =>
                      (t.players || []).map((p) => ({
                        id: p.id,
                        name: p.name,
                        team_id: t.id,
                        team_name: t.name,
                        isPrimaryTeam: t.id === resultFixture.home_team.id,
                      }))
                    )
                    .sort((a, b) => (b.isPrimaryTeam ? 1 : 0) - (a.isPrimaryTeam ? 1 : 0)),
                },
                away_team: {
                  ...resultFixture.away_team,
                  club_id: resultFixture.away_team.club_id,
                  players: teams
                    .filter((t) => t.club_id === resultFixture.away_team.club_id)
                    .flatMap((t) =>
                      (t.players || []).map((p) => ({
                        id: p.id,
                        name: p.name,
                        team_id: t.id,
                        team_name: t.name,
                        isPrimaryTeam: t.id === resultFixture.away_team.id,
                      }))
                    )
                    .sort((a, b) => (b.isPrimaryTeam ? 1 : 0) - (a.isPrimaryTeam ? 1 : 0)),
                },
                match_date: resultFixture.match_date,
                result: resultFixture.result ?? null,
                division_id: selectedDivision?.id || '',
                season_id: seasonId,
                all_teams: teams.map((team) => ({
                  ...team,
                  players: team.players || [],
                }))
              }}
              onClose={() => setResultFixture(null)}
            />
          </DialogContent>
        </Dialog>
      )}

      {isAddFixtureOpen && selectedTeam && seasonId && (
        <AddHomeFixtureModal
          isOpen={isAddFixtureOpen}
          onOpenChange={(open) => {
            setIsAddFixtureOpen(open);
            if (!open) setFixtureToEdit(null);
          }}
          homeTeamId={selectedTeam}
          divisionId={selectedDivision?.id || ''}
          seasonId={seasonId}
          teams={teams}
          onFixtureAdded={handleFixtureAdded}
          fixtureToEdit={fixtureToEdit}
        />
      )}
    </div>
    </>
  );
};

export default FixturesPage;